/**
 * The Genre Mixer's creative controls: five selects and the energy slider.
 *
 * Extracted for size. The `lyx-grid-2` class, the 2-column flow and the slider's
 * 10px top margin are verbatim.
 */
import { Select } from '../forms';
import { GM_LANGUAGES, GM_LENGTHS, GM_MOODS, GM_TEMPOS, GM_VOCAL_TYPES } from '../../data/music';
import { c } from '../../styles/theme';
import type { MixerSettings } from '../../types/domain';

/** The settings this panel edits. */
type EditableKey = 'mood' | 'tempo' | 'language' | 'vocal' | 'length' | 'energy';

export interface MixerControlsProps {
  settings: Pick<MixerSettings, EditableKey>;
  /**
   * Overloaded across the six keys rather than typed as (key, value: unknown),
   * so passing a string to `energy` is a compile error.
   */
  onChange: <K extends EditableKey>(key: K, value: MixerSettings[K]) => void;
}

export default function MixerControls({ settings, onChange }: MixerControlsProps) {
  const { mood, tempo, language, vocal, length, energy } = settings;

  return (
    <>
      {/* An eyebrow, which theme.ts files under text2 by name. */}
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>Creative controls</p>
      <div className="lyx-grid-2" style={{ marginBottom: 12 }}>
        <Select
          label="Mood"
          value={mood}
          onChange={(v) => onChange('mood', v)}
          options={GM_MOODS}
        />
        <Select
          label="Tempo"
          value={tempo}
          onChange={(v) => onChange('tempo', v)}
          options={GM_TEMPOS}
        />
        <Select
          label="Language"
          value={language}
          onChange={(v) => onChange('language', v)}
          options={GM_LANGUAGES}
        />
        <Select
          label="Vocal type"
          value={vocal}
          onChange={(v) => onChange('vocal', v)}
          options={GM_VOCAL_TYPES}
        />
        <Select
          label="Song length"
          value={length}
          onChange={(v) => onChange('length', v)}
          options={GM_LENGTHS}
        />
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 6,
            }}
          >
            <label htmlFor="gm-energy" style={{ fontSize: 12, color: c.text2, margin: 0 }}>
              Energy
            </label>
            {/* The slider's value readout — the only place the number is shown, so it
                has to be legible next to the label it belongs to. */}
            <span style={{ fontSize: 11, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
              {energy}%
            </span>
          </div>
          <input
            id="gm-energy"
            type="range"
            min={0}
            max={100}
            value={energy}
            onChange={(e) => onChange('energy', Number(e.target.value))}
            style={{ width: '100%', accentColor: c.accentFill, marginTop: 10 }}
          />
        </div>
      </div>
    </>
  );
}
